/**
 * Group-Classes Calendar component (shared by Admin + User views).
 *
 * Responsibilities:
 *  - Render a Mon-Sun month grid with event pills on day cells.
 *  - Provide a list view toggle.
 *  - Highlight "today" in the *user's local timezone* (red filled circle).
 *  - Notify parent of the visible date range so it can refetch events.
 *  - Call back when the user clicks an event pill or a "+" / day cell (admin).
 *
 * Time logic:
 *  - Events carry `start_at` as an ISO-8601 UTC string from the backend.
 *  - We convert to the browser's local TZ (resolved via Intl) when bucketing
 *    by day, formatting time labels, and computing "is today".
 */
import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, List, Calendar as CalendarIcon, Video, Lock, Check, Users, MonitorPlay } from 'lucide-react';
import api from '../lib/api';
import { useTrial } from '../contexts/TrialContext';
import { goPro } from '../lib/checkout';
import { isMobileNow } from '../lib/useIsMobile';
import { ClassInvitesPanel } from './events/ClassInvitesPanel';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Resolve a clean short label for the user's timezone (e.g. "EST", "PST",
// "UTC"). Falls back to the IANA city segment if the runtime doesn't expose
// timeZoneName parts.
function resolveTzLabel() {
  try {
    const parts = new Intl.DateTimeFormat([], { timeZoneName: 'short' }).formatToParts(new Date());
    const tz = parts.find(p => p.type === 'timeZoneName')?.value;
    if (tz && tz.length <= 6) return tz;
  } catch {}
  return USER_TZ.split('/').pop().replace(/_/g, ' ');
}
const USER_TZ_LABEL = resolveTzLabel();

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function isSameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
// Mon=0..Sun=6 (the calendar starts on Monday in the design)
function dowMon0(d) { const js = d.getDay(); return (js + 6) % 7; }

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: date.getMinutes() ? '2-digit' : undefined, hour12: true }).toLowerCase().replace(' ', '');
}
function formatTimeAndZone(date) {
  const t = formatTime(date);
  return `${t} ${USER_TZ_LABEL} (${USER_TZ})`;
}

// Auto-prepend `https://` to URLs the admin pasted without a protocol
// (e.g. `zoom.us/j/123` → `https://zoom.us/j/123`). Without this, the
// browser treats the href as a relative path and lands on a 404 like
// `https://celpipmax.com/zoom.us/j/123`. Internal app paths (starting
// with `/`) and `mailto:` / `tel:` URIs are passed through unchanged.
function ensureProtocol(url) {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed; // has scheme
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (trimmed.startsWith('/')) return trimmed; // internal SPA path
  return `https://${trimmed}`;
}

function TierBadge({ tier }) {
  const { entryLabel } = useTrial();
  if (tier === 'pro_only') return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700">Pro</span>;
  if (tier === 'free_only') return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">{entryLabel}</span>;
  return null;
}
const tierBadge = (tier) => <TierBadge tier={tier} />;

/** live = running right now · soon = starts within 30 min (room already open) · past = ended · upcoming. */
export function eventState(ev, now = new Date()) {
  const start = new Date(ev.start_at);
  const end = ev.end_at ? new Date(ev.end_at) : new Date(start.getTime() + (ev.duration_minutes || 60) * 60_000);
  if (now >= start && now < end) return 'live';
  if (now >= end) return 'past';
  if (start - now <= 30 * 60_000) return 'soon';
  return 'upcoming';
}
export const minutesLeft = (ev, now = new Date()) => Math.max(0, Math.round(((ev.end_at ? new Date(ev.end_at) : new Date(new Date(ev.start_at).getTime() + (ev.duration_minutes || 60) * 60_000)) - now) / 60_000));
export function LiveBadge({ className = '', testId = 'event-live-badge' }) {
  return <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider bg-rose-600 text-white ${className}`} data-testid={testId}><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live now</span>;
}

export function CalendarHeader({ cursor, onPrev, onNext, onToday, onAdd, view, setView, isAdmin, now }) {
  const monthLabel = cursor.toLocaleString([], { month: 'long', year: 'numeric' });
  const timeNow = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-2.5 sm:p-4 flex items-center justify-between gap-2 sm:gap-3 shadow-sm" data-testid="cal-header">
      <button onClick={onToday} className="px-3 sm:px-4 py-1.5 border border-slate-300 rounded-full text-xs sm:text-sm text-slate-600 hover:bg-slate-50 transition shrink-0" data-testid="cal-today-btn">Today</button>
      <div className="flex items-center gap-1 sm:gap-2 flex-1 justify-center min-w-0">
        <button onClick={onPrev} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition shrink-0" data-testid="cal-prev-btn"><ChevronLeft size={18} /></button>
        <div className="text-center min-w-0 sm:min-w-[180px]">
          <div className="font-[Manrope] text-base sm:text-xl font-bold tracking-tight text-slate-900 truncate" data-testid="cal-month-label">{monthLabel}</div>
          <div className="text-[10px] sm:text-[11px] text-slate-400 truncate">{timeNow} {USER_TZ_LABEL} <span className="hidden sm:inline text-slate-300">({USER_TZ})</span></div>
        </div>
        <button onClick={onNext} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition shrink-0" data-testid="cal-next-btn"><ChevronRight size={18} /></button>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isAdmin && (
          <button onClick={onAdd} className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-slate-200 transition" title="Add event" data-testid="cal-add-event-btn"><Plus size={16} /></button>
        )}
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          <button onClick={() => setView('list')} className={`p-2 transition ${view === 'list' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50'}`} title="List view" data-testid="cal-view-list"><List size={16} /></button>
          <button onClick={() => setView('grid')} className={`p-2 transition ${view === 'grid' ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:bg-slate-50'}`} title="Calendar view" data-testid="cal-view-grid"><CalendarIcon size={16} /></button>
        </div>
      </div>
    </div>
  );
}

export function MonthGrid({ cursor, events, onEventClick, now }) {
  const first = startOfMonth(cursor);
  const last = endOfMonth(cursor);
  const startOffset = dowMon0(first);
  const totalCells = Math.ceil((startOffset + last.getDate()) / 7) * 7;

  // Bucket events by local-day key "YYYY-MM-DD"
  const byDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = new Date(ev.start_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      (map[key] ||= []).push(ev);
    }
    // Sort each day's events by time
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.start_at) - new Date(b.start_at)));
    return map;
  }, [events]);

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startOffset + 1;
    const inMonth = dayNum >= 1 && dayNum <= last.getDate();
    const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), dayNum);
    const key = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(cellDate.getDate()).padStart(2, '0')}`;
    const dayEvents = inMonth ? (byDay[key] || []) : [];
    const isToday = inMonth && isSameLocalDay(cellDate, now);
    cells.push({ inMonth, dayNum, isToday, events: dayEvents, key: `c${i}` });
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="cal-month-grid">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {WEEKDAYS.map(d => (
          <div key={d} className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-1.5 sm:px-3 py-2 border-r last:border-r-0 border-slate-200 text-center sm:text-left">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map(c => (
          <div key={c.key} className={`min-h-[68px] sm:min-h-[120px] border-r border-b last:border-r-0 border-slate-200 p-1 sm:p-1.5 flex flex-col gap-1 ${c.inMonth ? 'bg-white' : 'bg-slate-50/50'}`}>
            {c.inMonth ? (
              <>
                {c.isToday ? (
                  <div className="self-end w-7 h-7 flex items-center justify-center rounded-full bg-red-500 text-white font-bold text-sm" data-testid="cal-today-highlight">{c.dayNum}</div>
                ) : (
                  <div className="self-end text-sm font-medium text-slate-700 px-1 py-0.5">{c.dayNum}</div>
                )}
                <div className="flex flex-col gap-1 overflow-hidden">
                  {c.events.slice(0, 3).map(ev => {
                    const d = new Date(ev.start_at);
                    const state = eventState(ev, now);
                    const locked = !!ev.is_locked;
                    const baseClasses = state === 'live'
                      ? 'bg-rose-600 text-white hover:bg-rose-700 ring-2 ring-rose-300 ring-offset-1 shadow-md font-semibold'
                      : state === 'past'
                        ? 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                        : locked
                          ? 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
                          : state === 'soon'
                            ? 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-300'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100';
                    return (
                      <button
                        key={ev.id}
                        onClick={() => onEventClick(ev)}
                        className={`flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-left truncate transition cursor-pointer ${baseClasses}`}
                        data-testid={`event-pill-${ev.id}`}
                        data-locked={locked ? 'true' : 'false'}
                        data-state={state}
                        title={state === 'live' ? `${ev.title} — live now, ${minutesLeft(ev, now)} min left` : locked ? `${ev.title} — Pro only` : ev.title}
                      >
                        {state === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shrink-0" />}
                        {locked && state !== 'live' && <Lock size={9} className="shrink-0" />}
                        <span className="font-semibold whitespace-nowrap">{state === 'live' ? 'LIVE' : formatTime(d)}</span>
                        <span className="truncate hidden sm:inline">{ev.title}</span>
                      </button>
                    );
                  })}
                  {c.events.length > 3 && (
                    <button onClick={() => onEventClick(c.events[3])} className="text-[10px] text-slate-400 hover:text-slate-600 self-start">+{c.events.length - 3} more</button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function EventListView({ events, onEventClick, now }) {
  if (!events.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="cal-empty-state">
        <CalendarIcon size={32} className="mx-auto text-slate-300 mb-3" />
        <div className="text-sm text-slate-500 font-medium">No upcoming classes yet</div>
        <div className="text-xs text-slate-400 mt-1">Check back soon — new classes are added regularly.</div>
      </div>
    );
  }
  // What's next comes first; classes that already ended sit under a divider at the bottom.
  const upcoming = events.filter(ev => eventState(ev, now) !== 'past');
  const pastEvents = events.filter(ev => eventState(ev, now) === 'past');
  const row = (ev) => {
        const d = new Date(ev.start_at);
        const state = eventState(ev, now);
        const past = state === 'past';
        const live = state === 'live';
        const locked = !!ev.is_locked;
        return (
          <button
            key={ev.id}
            onClick={() => onEventClick(ev)}
            className={`w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 text-left transition ${past ? 'opacity-60' : ''} ${live ? 'bg-rose-50/70 ring-2 ring-inset ring-rose-300 hover:bg-rose-50' : locked ? 'hover:bg-amber-50/40' : 'hover:bg-slate-50'}`}
            data-testid={`event-row-${ev.id}`}
            data-locked={locked ? 'true' : 'false'}
            data-state={state}
          >
            <div className={`flex flex-col items-center justify-center rounded-lg p-2 sm:p-3 min-w-[58px] sm:min-w-[68px] border shrink-0 ${live ? 'bg-rose-600 text-white border-rose-600' : locked ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
              <span className="text-[10px] font-semibold uppercase">{d.toLocaleString([], { month: 'short' })}</span>
              <span className="text-xl sm:text-2xl font-bold tracking-tight leading-none">{d.getDate()}</span>
              <span className={`text-[10px] mt-1 ${live ? 'text-rose-100' : locked ? 'text-amber-600' : 'text-blue-600'}`}>{formatTime(d)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 text-sm sm:text-base leading-snug">{ev.title}</span>
                {live && <LiveBadge testId={`event-live-badge-${ev.id}`} />}
                {state === 'soon' && !locked && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700" data-testid={`event-soon-badge-${ev.id}`}>Starting soon</span>}
                {tierBadge(ev.access_tier)}
                {locked && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200"><Lock size={9} /> Upgrade to join</span>}
                {!locked && ev.rsvp_status === 'attending' && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700"><Check size={10} /> Attending</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                <Video size={11} className={live ? 'text-rose-500' : 'text-blue-500'} /> {ev.location || 'Zoom'} · {live ? `${minutesLeft(ev, now)} min left — tap to join` : `${ev.duration_minutes} min`}
                {typeof ev.attendee_count === 'number' && ev.attendee_count > 0 && (
                  <span className="inline-flex items-center gap-1 text-slate-400"><Users size={11} /> {ev.attendee_count}</span>
                )}
              </div>
              {ev.description && <div className="text-xs text-slate-400 mt-1 truncate hidden sm:block">{ev.description}</div>}
            </div>
          </button>
        );
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="cal-list">
      {upcoming.map(row)}
      {upcoming.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="cal-list-no-upcoming">Nothing more this month — use › for next month's classes.</div>}
      {pastEvents.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-400" data-testid="cal-list-past-divider">Earlier this month · {pastEvents.length}</div>
      )}
      {pastEvents.map(row)}
    </div>
  );
}

/**
 * Top-level Calendar shell: header + grid/list. Consumers pass `events`, `onEventClick`,
 * `onAdd`, and an `onRangeChange` callback so they can refetch when month changes.
 */
export default function CalendarShell({ events, loading, onEventClick, onAdd, isAdmin = false, onRangeChange }) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  // Phones open on the agenda list — a 7-column month grid is unreadable at 390 px.
  const [view, setView] = useState(() => (isMobileNow() ? 'list' : 'grid'));
  const [now, setNow] = useState(new Date());

  // Live tick the "now" reference every 60s so the today highlight rolls over after midnight.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Notify parent when the visible range changes, so it can fetch the right window.
  useEffect(() => {
    if (!onRangeChange) return;
    // Fetch a generous window: 1 week before, 6 weeks after, to cover the visible grid + recurrences.
    const from = new Date(cursor.getFullYear(), cursor.getMonth(), -7);
    const to = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 7);
    onRangeChange(from.toISOString(), to.toISOString());
  }, [cursor, onRangeChange]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-4">
      <CalendarHeader
        cursor={cursor}
        onPrev={() => setCursor(addMonths(cursor, -1))}
        onNext={() => setCursor(addMonths(cursor, 1))}
        onToday={() => setCursor(startOfMonth(new Date()))}
        onAdd={onAdd}
        view={view}
        setView={setView}
        isAdmin={isAdmin}
        now={now}
      />
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-sm text-slate-400">Loading classes…</div>
      ) : view === 'grid' ? (
        <MonthGrid cursor={cursor} events={events} onEventClick={onEventClick} now={now} />
      ) : (
        <EventListView events={events} onEventClick={onEventClick} now={now} />
      )}
    </motion.div>
  );
}

// ── Event Detail Modal (read-only view, shared by user + admin) ────────
export function EventDetailModal({ event, onClose, onEdit, onDelete, isAdmin, onRsvpChange, onRoster }) {
  const navigate = useNavigate();
  const { entryLabel } = useTrial();
  const [menuOpen, setMenuOpen] = useState(false);
  const [roomBusy, setRoomBusy] = useState(false);
  const [inApp, setInApp] = useState(!!event?.in_app);
  useEffect(() => { setInApp(!!event?.in_app); }, [event]);
  const createRoom = async () => {
    setRoomBusy(true);
    try { await api.post(`/api/admin/zoom/meetings/class/${event.series_id || event.id}`); setInApp(true); toast.success('In-app Zoom room created — students join from this page'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create the Zoom room'); }
    setRoomBusy(false);
  };
  // Local RSVP state — initialised from the event payload, mutated by toggle
  // so the modal can update instantly while the parent refetches in the
  // background.
  const [rsvpStatus, setRsvpStatus] = useState(event?.rsvp_status || null);
  const [attendeeCount, setAttendeeCount] = useState(event?.attendee_count || 0);
  const [rsvpSaving, setRsvpSaving] = useState(false);
  // Admin attendees panel
  const [showAttendees, setShowAttendees] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [attendeesMeta, setAttendeesMeta] = useState({ missing: 0, joined: 0 });
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  // Reset local state when the event prop changes (e.g. user opens a different event).
  useEffect(() => {
    setRsvpStatus(event?.rsvp_status || null);
    setAttendeeCount(event?.attendee_count || 0);
    setShowAttendees(false);
  }, [event?.id, event?.rsvp_status, event?.attendee_count]);

  if (!event) return null;
  const start = new Date(event.start_at);
  const end = new Date(start.getTime() + (event.duration_minutes || 30) * 60_000);
  const dateLine = `${start.toLocaleString([], { weekday: 'long', month: 'short', day: 'numeric' })} @ ${formatTime(start)} - ${formatTime(end)}`;
  const past = end < new Date();
  const live = start <= new Date() && !past;
  const tier = event.access_tier || 'all';

  const toggleRsvp = async () => {
    if (past || rsvpSaving) return;
    if (event.is_locked) {
      // Defensive — should never happen because the button is hidden,
      // but if some legacy keyboard or extension fires it anyway, open
      // the Pro upgrade window instead of swallowing a 403.
      goPro();
      return;
    }
    setRsvpSaving(true);
    try {
      const isAttending = rsvpStatus === 'attending';
      const { data } = isAttending
        ? await api.delete(`/api/events/${event.id}/rsvp`)
        : await api.post(`/api/events/${event.id}/rsvp`);
      setRsvpStatus(data.rsvp_status);
      setAttendeeCount(data.attendee_count);
      onRsvpChange?.(event.id, data);
    } catch (err) {
      console.error('RSVP failed', err);
      alert(err.response?.data?.detail || 'RSVP failed');
    } finally {
      setRsvpSaving(false);
    }
  };

  const loadAttendees = async () => {
    setLoadingAttendees(true);
    try {
      const { data } = await api.get(`/api/admin/events/${event.id}/attendees`);
      setAttendees(data?.attendees || []);
      setAttendeesMeta({ missing: data?.missing || 0, joined: data?.joined || 0 });
      setAttendeeCount(data?.count ?? (data?.attendees || []).length);
      setShowAttendees(true);
    } catch (err) {
      console.error('Load attendees failed', err);
      alert('Failed to load attendees.');
    } finally {
      setLoadingAttendees(false);
    }
  };

  const makeIcs = () => {
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
    const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nUID:${event.id}@celpipmax\nDTSTAMP:${fmt(new Date())}\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nSUMMARY:${event.title}\nDESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}\\n\\nJoin: ${event.zoom_link || ''}\nLOCATION:${event.zoom_link || event.location || ''}\nEND:VEVENT\nEND:VCALENDAR`;
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="event-detail-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[92vh] flex flex-col"
      >
        {/* Cover */}
        <div className="relative h-32 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center shrink-0">
          {event.cover_image_url
            ? <img src={event.cover_image_url} alt="" className="w-full h-full object-cover" />
            : <CalendarIcon size={48} className="text-blue-300" />}
          {isAdmin && (
            <div className="absolute top-3 right-3">
              <button onClick={() => setMenuOpen(o => !o)} className="w-8 h-8 rounded-full bg-white/90 hover:bg-white text-slate-700 flex items-center justify-center shadow-sm" aria-label="Class actions" aria-haspopup="menu" aria-expanded={menuOpen} data-testid="event-admin-menu">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden w-48">
                  <button onClick={() => { setMenuOpen(false); onEdit(event); }} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="event-edit-btn">Edit event</button>
                  {onRoster && <button onClick={() => { setMenuOpen(false); onRoster(event); }} className="block w-full text-left px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-50" data-testid="event-roster-btn">Attendance &amp; grants</button>}
                  <button onClick={() => { setMenuOpen(false); loadAttendees(); }} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="event-view-attendees-btn">View attendees ({attendeeCount})</button>
                  <button onClick={() => { setMenuOpen(false); onDelete(event); }} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-testid="event-delete-btn">Delete event</button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="text-xl font-bold text-slate-900 leading-tight">{event.title}</h3>
              {tierBadge(tier)}
              {live && <LiveBadge />}
              {past && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-500">Past</span>}
            </div>
            <div className="flex items-start gap-2 mt-3">
              <CalendarIcon size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium text-slate-700">{dateLine}</div>
                <div className="text-xs text-slate-400">{USER_TZ_LABEL} <span className="text-slate-300">({USER_TZ})</span></div>
              </div>
            </div>
            {inApp && !event.is_locked && (
              <div className="flex items-center gap-2 mt-2" data-testid="event-inapp-room">
                <MonitorPlay size={14} className="text-teal-600 shrink-0" />
                <span className="text-sm text-teal-800 font-medium">Live inside CELPIPMax — no Zoom link needed{isAdmin && event.zoom_meeting_id ? <span className="text-slate-400 font-normal"> · room #{event.zoom_meeting_id}</span> : null}</span>
              </div>
            )}
            {!inApp && event.zoom_link && !event.is_locked && (
              <div className="flex items-center gap-2 mt-2">
                <Video size={14} className="text-blue-500 shrink-0" />
                <a href={ensureProtocol(event.zoom_link)} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate" data-testid="event-zoom-link">{event.zoom_link}</a>
              </div>
            )}
            {/* Attendee count badge — visible to everyone */}
            <div className="flex items-center gap-2 mt-2">
              <Users size={14} className="text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500" data-testid="event-attendee-count">
                {attendeeCount === 0 ? 'Be the first to RSVP' : `${attendeeCount} attending`}
              </span>
            </div>
          </div>
          {event.description && (
            <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{event.description}</div>
          )}
          {/* Admin attendees inline panel — seats saved (RSVP) only */}
          {isAdmin && showAttendees && (
            <div className="border border-slate-200 rounded-lg bg-slate-50/50 p-3" data-testid="event-attendees-panel">
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="event-attendees-title">Saved a seat ({attendees.length}){attendeesMeta.joined ? <span className="normal-case tracking-normal font-normal text-emerald-700"> · {attendeesMeta.joined} joined</span> : null}</span>
                <button onClick={() => setShowAttendees(false)} className="text-xs text-slate-400 hover:text-slate-600">Hide</button>
              </div>
              {loadingAttendees ? (
                <div className="text-xs text-slate-400">Loading…</div>
              ) : attendees.length === 0 ? (
                <div className="text-xs text-slate-400">No seats saved yet.</div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {attendees.map(a => (
                    <div key={a.user_id} className="flex items-center gap-2 text-xs" data-testid={`event-attendee-${a.user_id}`}>
                      <span className="font-mono px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100 shrink-0">{a.student_id || '—'}</span>
                      <span className="font-medium text-slate-700 truncate">{a.name || a.email}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${a.subscription_status !== 'free' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{a.is_staff ? 'staff' : a.subscription_status === 'free' ? entryLabel.toLowerCase() : a.subscription_status}</span>
                      {a.joined && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700 ml-auto shrink-0" data-testid={`event-attendee-joined-${a.user_id}`}><Check size={10} /> joined</span>}
                    </div>
                  ))}
                </div>
              )}
              {attendeesMeta.missing > 0 && <p className="text-[11px] text-slate-400 mt-2" data-testid="event-attendees-missing">{attendeesMeta.missing} seat{attendeesMeta.missing === 1 ? '' : 's'} belonged to accounts that no longer exist — not counted.</p>}
              <p className="text-[11px] text-slate-400 mt-2">Students who joined without saving a seat, class-code users and manual marks are under <b>Attendance &amp; grants</b>.</p>
            </div>
          )}
          {isAdmin && <ClassInvitesPanel eventId={event.id} />}
          <div className="flex flex-col gap-2 pt-2">
            {/* Locked Pro-only event: Free users see only an upgrade CTA — RSVP + Join are hidden */}
            {!isAdmin && !past && event.is_locked && (
              <button
                onClick={() => goPro()}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition"
                data-testid="event-upgrade-btn"
              >
                <Lock size={16} /> Upgrade to Pro to join
              </button>
            )}
            {/* RSVP toggle — non-admin users on future, NON-locked events */}
            {!isAdmin && !past && !event.is_locked && (
              <button
                onClick={toggleRsvp}
                disabled={rsvpSaving}
                className={`w-full font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition text-sm border ${
                  rsvpStatus === 'attending'
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                } disabled:opacity-50`}
                data-testid="event-rsvp-btn"
              >
                {rsvpStatus === 'attending' ? <><Check size={15} /> You're attending — tap to cancel</> : <>I'll attend</>}
              </button>
            )}
            {inApp && !event.is_locked && (
              <button type="button" onClick={() => navigate(`/live/class/${event.id}`)} className={`w-full text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition ${live ? 'bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/25' : 'bg-teal-700 hover:bg-teal-800'}`} data-testid="event-join-inapp-btn" data-live={live ? 'true' : 'false'}>
                <MonitorPlay size={16} /> {isAdmin ? (live ? 'Class is live — open as host' : 'Start class as host') : live ? `Join now — class is live (${minutesLeft(event)} min left)` : past ? 'Open the room' : 'Join class here'}
              </button>
            )}
            {isAdmin && !inApp && !past && (
              <button type="button" onClick={createRoom} disabled={roomBusy} className="w-full border border-teal-300 text-teal-800 bg-teal-50 hover:bg-teal-100 font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition text-sm disabled:opacity-50" data-testid="event-create-room-btn">
                <MonitorPlay size={15} /> {roomBusy ? 'Creating…' : 'Create in-app Zoom room (auto attendance + recording)'}
              </button>
            )}
            {!inApp && event.zoom_link && !past && !event.is_locked && (
              <a href={ensureProtocol(event.zoom_link)} target="_blank" rel="noopener noreferrer" className={`w-full text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition ${live ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'}`} data-testid="event-join-btn" data-live={live ? 'true' : 'false'}>
                <Video size={16} /> {live ? 'Join now — class is live' : 'Join Class'}
              </a>
            )}
            <button onClick={makeIcs} className="w-full border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition text-sm" data-testid="event-add-to-calendar-btn">
              <CalendarIcon size={14} /> Add to my calendar
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── "Locked for free users" little CTA (used if we ever want to render
//    pro-only events as visible-but-locked instead of hidden) ──────────
export function LockedEventBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">
      <Lock size={9} /> Pro
    </span>
  );
}
